import React, { useMemo, useState } from 'react'
import { css } from '@emotion/react'
import moment from 'moment'
import {
  colors,
  fontSize,
  type GridColumns,
  Modal,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import { SVGIcon } from 'ic-snacks'
import { useDashMessages } from 'intl/intl.hooks'
import Link from 'legacy/components/Link'
import { dateFormat } from 'domains/merchandising/constants/heroBanner.constants'

export interface MerchandisingHeroBannersListDatum {
  name: string
  preview: React.Component
  platform: ReadonlyArray<string>
  startDate: string
  endDate: string
  status: string
}

const imageStyles = css({
  height: '60px',
  border: `1px ${colors.GRAYSCALE.X30} solid`,
})

const statusHeaderStyles = css({
  display: 'flex',
  '& > svg': {
    marginRight: spacing.X4,
  },
})

const statusTitleStyles = css({
  fontSize: fontSize.X15,
  marginRight: 0,
  textTransform: 'uppercase',
})

export const useHeroBannersListColumns = () => {
  const messages = useDashMessages({
    date: 'merchandising.hero-banners.columns.date.header',
    name: 'merchandising.hero-banners.columns.name.header',
    platform: 'merchandising.hero-banners.columns.platform.header',
    status: 'merchandising.hero-banners.columns.status.header',
    emptyValue: 'merchandising.hero-banners.field.empty.value',
    preview: 'merchandising.hero-banners.columns.preview.header',
    liveDefinition: 'merchandising.hero-banners.status.live.definition',
    liveTitle: 'merchandising.hero-banners.status.live',
    activeDefinition: 'merchandising.hero-banners.status.active.definition',
    activeTitle: 'merchandising.hero-banners.status.active',
    inactiveDefinition: 'merchandising.hero-banners.status.inactive.definition',
    inactiveTitle: 'merchandising.hero-banners.status.inactive',
  })

  const [statusTooltipVisible, setStatusTooltipVisible] = useState(false)

  const columns: GridColumns<MerchandisingHeroBannersListDatum> = useMemo(() => {
    const columnHeaders = [
      {
        Cell: ({ original, column }) => {
          const { url } = column.getProps().table
          const goToDetailsPage = url('merchandising-hero-banners-details', {
            heroBannerId: original?.id,
          })

          return original?.name && <Link to={goToDetailsPage}>{original.name}</Link>
        },
        header: messages.name,
        id: 'name',
      },
      {
        Cell: ({ original }) =>
          original?.images && <img css={imageStyles} src={original.images.large} />,
        header: messages.preview,
        id: 'preview',
      },
      {
        Cell: ({ original }) => original?.platform.join(', '),
        header: messages.platform,
        id: 'platform',
      },
      {
        Cell: ({ original }) => {
          const { startDate, endDate } = original?.scheduling || {}
          const start = startDate ? moment(startDate).format(dateFormat) : messages.emptyValue
          const end = endDate ? moment(endDate).format(dateFormat) : messages.emptyValue

          return `${start} - ${end}`
        },
        header: messages.date,
        id: 'date',
      },
      {
        Cell: ({ original }) => original?.status,
        header: (
          <>
            <div css={statusHeaderStyles} onClick={() => setStatusTooltipVisible(true)}>
              <SVGIcon name="infoFilled" size="small" />
              {messages.status}
            </div>
            <Modal
              isOpen={statusTooltipVisible}
              headerContent={
                <Text size="large" weight="bold">
                  {messages.status}
                </Text>
              }
              maxWidth={640}
              bodyContent={
                <>
                  <p>
                    <Text display="inline" weight="bold" styles={statusTitleStyles}>
                      {messages.liveTitle + ' - '}
                    </Text>
                    {messages.liveDefinition}
                  </p>
                  <p>
                    <Text display="inline" weight="bold" styles={statusTitleStyles}>
                      {messages.activeTitle + ' - '}
                    </Text>
                    {messages.activeDefinition}
                  </p>
                  <p>
                    <Text display="inline" weight="bold" styles={statusTitleStyles}>
                      {messages.inactiveTitle + ' - '}
                    </Text>
                    {messages.inactiveDefinition}
                  </p>
                </>
              }
              onRequestClose={() => setStatusTooltipVisible(!statusTooltipVisible)}
            />
          </>
        ),
        id: 'status',
      },
      {
        Header: () => null,
        width: 30,
        id: 'edit',
        Cell: ({ original, column }) => {
          const { url } = column.getProps().table
          const goToEditPage = url('merchandising-hero-banners-details', {
            heroBannerId: original.id,
          })

          return (
            <div>
              <Link to={goToEditPage}>
                <SVGIcon name="pencil" color={colors.PRIMARY.REGULAR} />
              </Link>
            </div>
          )
        },
      },
    ]

    return columnHeaders
  }, [messages, statusTooltipVisible])

  return columns
}
